import { generateUUID } from '../utils/uuid';
/**
 * SmartLaunch — SMART on FHIR authorization code flow (OpenEMR 8.0.0).
 *
 * Discovers authorize/token endpoints from the FHIR capability statement
 * rather than constructing them manually — this is the standards-compliant
 * approach and avoids URL mismatch issues that plagued v7.
 */
import { useEffect, useState } from 'react';

let FHIR_URL = import.meta.env.VITE_SMART_FHIR_URL ?? '';
let CLIENT_ID = import.meta.env.VITE_SMART_CLIENT_ID ?? '';
let CLIENT_SECRET = import.meta.env.VITE_SMART_CLIENT_SECRET ?? '';
let REDIRECT_URI = import.meta.env.VITE_SMART_REDIRECT_URI ?? '';
let CONFIGURED_SCOPE = import.meta.env.VITE_SMART_SCOPE ?? '';

async function loadRuntimeConfig() {
  try {
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    const res = await fetch(`${base}/smart-config.json`);
    if (!res.ok) return;
    const cfg = await res.json();
    CLIENT_ID = cfg.clientId ?? CLIENT_ID;
    CLIENT_SECRET = cfg.clientSecret ?? CLIENT_SECRET;
    FHIR_URL = cfg.fhirUrl ?? FHIR_URL;
    REDIRECT_URI = cfg.redirectUri ?? REDIRECT_URI;
    CONFIGURED_SCOPE = cfg.scope ?? CONFIGURED_SCOPE;
  } catch { /* no runtime config — use build-time vars */ }
}

function defaults() {
  if (!FHIR_URL) FHIR_URL = `${window.location.origin}/apis/default/fhir`;
  if (!REDIRECT_URI) REDIRECT_URI = `${window.location.origin}${import.meta.env.BASE_URL.replace(/\/$/, '')}/callback`;
}

function hasCachedToken(): boolean {
  const token = localStorage.getItem('smart_access_token');
  const expiry = localStorage.getItem('smart_token_expiry');
  if (!token || !expiry) return false;
  return Date.now() < Number(expiry) - 2 * 60 * 1000;
}

/** Derives the token endpoint from the stored ISS without a metadata round-trip. */
function getTokenUrlFromIss(iss: string): string {
  return iss.replace(/\/apis\/.*$/, '') + '/oauth2/default/token';
}

/**
 * Tries to exchange the stored refresh token for a new access token.
 * Stores the new tokens on success and returns the new patient ID (may be empty).
 * Throws if the refresh fails (caller should fall back to full OAuth).
 */
async function tryRefreshToken(iss: string): Promise<string> {
  const refreshToken = localStorage.getItem('smart_refresh_token');
  if (!refreshToken) throw new Error('No refresh token');

  const tokenUrl = getTokenUrlFromIss(iss);
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: CLIENT_ID,
  });
  if (CLIENT_SECRET) body.set('client_secret', CLIENT_SECRET);

  const res = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Refresh failed (${res.status}): ${text}`);
  }

  const data = await res.json();
  if (!data.access_token) throw new Error('No access_token in refresh response');

  localStorage.setItem('smart_access_token', data.access_token);
  if (data.refresh_token) localStorage.setItem('smart_refresh_token', data.refresh_token);
  const expiresIn = data.expires_in ?? 3600;
  localStorage.setItem('smart_token_expiry', String(Date.now() + expiresIn * 1000));

  // Keep chrome.storage.local in sync so content script always has a fresh token
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cr = (window as any).chrome;
    if (cr?.storage?.local) cr.storage.local.set({ smart_access_token: data.access_token, smart_fhir_url: iss });
  } catch { /* extension not installed */ }

  // Return the patient from the refresh response if provided, else the last known one
  return data.patient ?? localStorage.getItem('smart_last_patient') ?? '';
}

export default function SmartLaunch() {
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    async function launch() {
      try {
        await loadRuntimeConfig();
        defaults();

        const params = new URLSearchParams(window.location.search);
        const iss = params.get('iss') || FHIR_URL;
        const launchToken = params.get('launch');
        const base = import.meta.env.BASE_URL.replace(/\/$/, '');

        // ── Fast path: reuse cached/refreshed token ────────────────────────
        // When a launch token is present (fresh EHR click), we need the
        // patient UUID from it. Instead of full OAuth (which forces re-login),
        // ask OpenEMR to decode the encrypted launch token server-side.
        if (hasCachedToken() || localStorage.getItem('smart_refresh_token')) {
          let patient = '';

          // Extract patient from the launch token via server-side decryption
          if (launchToken) {
            try {
              const decodeUrl = iss.replace(/\/apis\/.*$/, '')
                + '/interface/smart/decode_launch.php?launch=' + encodeURIComponent(launchToken);
              const dr = await fetch(decodeUrl, { credentials: 'include' });
              if (dr.ok) {
                const decoded = await dr.json();
                patient = decoded.patient ?? '';
              }
            } catch { /* fall through to full OAuth */ }
          }

          // If no launch token (bookmark/reload), use last known patient
          if (!patient && !launchToken) {
            patient = localStorage.getItem('smart_last_patient') || '';
          }

          // Try refresh if access token expired
          if (!hasCachedToken() && localStorage.getItem('smart_refresh_token')) {
            try {
              await tryRefreshToken(iss);
            } catch {
              localStorage.removeItem('smart_refresh_token');
              localStorage.removeItem('smart_access_token');
              localStorage.removeItem('smart_token_expiry');
            }
          }

          // If we have a valid token and resolved patient, skip full OAuth
          if (hasCachedToken() && patient) {
            localStorage.setItem('smart_last_patient', patient);
            window.location.href = `${base}/app?mode=smart&theme=openemr&patientId=${patient}`;
            return;
          }
        }

        if (!CLIENT_ID) {
          throw new Error('VITE_SMART_CLIENT_ID not set. Run ./setup-openemr.sh first.');
        }

        // ── Step 1: Discover OAuth endpoints from FHIR metadata ──────────
        const metadataUrl = `${iss}/metadata`;
        const metaRes = await fetch(metadataUrl, {
          headers: { Accept: 'application/fhir+json' },
        });
        if (!metaRes.ok) {
          throw new Error(`Cannot reach FHIR server at ${metadataUrl} (${metaRes.status})`);
        }

        const metadata = await metaRes.json();
        const security = metadata.rest?.[0]?.security;
        const oauthUris = security?.extension?.find(
          (e: { url: string }) => e.url === 'http://fhir-registry.smarthealthit.org/StructureDefinition/oauth-uris'
        );

        let authorizeUrl = '';
        let tokenUrl = '';

        if (oauthUris?.extension) {
          for (const ext of oauthUris.extension) {
            if (ext.url === 'authorize') authorizeUrl = ext.valueUri;
            if (ext.url === 'token') tokenUrl = ext.valueUri;
          }
        }

        if (!authorizeUrl) {
          const baseUrl = iss.replace(/\/apis\/.*$/, '');
          authorizeUrl = `${baseUrl}/oauth2/default/authorize`;
          tokenUrl = `${baseUrl}/oauth2/default/token`;
        }

        const state = generateUUID();
        localStorage.setItem('smart_state', state);
        localStorage.setItem('smart_iss', iss);
        localStorage.setItem('smart_token_url', tokenUrl);

        // ── Step 2: Build authorization request ──────────────────────────
        const scope = CONFIGURED_SCOPE || (launchToken
          ? 'openid fhirUser offline_access launch api:fhir user/Patient.read user/Condition.read user/Observation.read user/Encounter.read user/MedicationRequest.read'
          : 'openid fhirUser offline_access launch/patient api:fhir user/Patient.read user/Condition.read user/Observation.read user/Encounter.read user/MedicationRequest.read');

        const authParams = new URLSearchParams({
          response_type: 'code',
          client_id: CLIENT_ID,
          redirect_uri: REDIRECT_URI,
          scope,
          state,
          aud: iss,
          ...(launchToken && { launch: launchToken }),
        });

        window.location.href = `${authorizeUrl}?${authParams.toString()}`;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Launch failed';
        setError(msg);
        setDetail(
          'Check that OpenEMR is running and the FHIR API is enabled. ' +
          'Admin > Config > Connectors > Enable FHIR REST API.'
        );
      }
    }
    launch();
  }, []);

  if (error) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif' }}>
      <div style={{ textAlign: 'center', padding: '40px', maxWidth: '520px' }}>
        <h2 style={{ color: '#DC3545' }}>Launch Failed</h2>
        <p style={{ color: '#6C757D' }}>{error}</p>
        {detail && <p style={{ color: '#999', fontSize: '13px', marginTop: '8px' }}>{detail}</p>}
      </div>
    </div>
  );

  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#F4F4F4' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #E2E8F0', borderTopColor: '#28A745', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: '#6C757D' }}>Connecting to OpenEMR...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
